import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save, CheckCircle2, User, MapPin, Mail, KeyRound, Eye, EyeOff, XCircle, Camera } from 'lucide-react';
import TierAvatar from './TierAvatar';
import { tidyProfile } from '@/lib/customerFormat';

const Field = ({ label, children }) => (
  <div>
    <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">{label}</Label>
    {children}
  </div>
);

const inputCls = "bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";

const SectionHead = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 mb-5 pb-3 border-b border-white/5">
    <Icon className="w-4 h-4 text-primary" />
    <p className="text-primary text-[10px] font-bold tracking-widest">{label}</p>
  </div>
);

export default function ProfileDetails({ user, onUserUpdate }) {
  const [uploadingPic, setUploadingPic] = useState(false);
  const [picError, setPicError] = useState('');
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    delivery_address_line1: user?.delivery_address_line1 || '',
    delivery_address_line2: user?.delivery_address_line2 || '',
    delivery_city: user?.delivery_city || '',
    delivery_postcode: user?.delivery_postcode || '',
  });
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  // Newsletter subscription status
  const [newsletterStatus, setNewsletterStatus] = useState('checking'); // checking | subscribed | not_subscribed
  const [newsletterBusy, setNewsletterBusy] = useState(false);

  // Change password
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPic(true);
    setPicError('');
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      await base44.auth.updateMe({ profile_picture_url: file_url });
      if (onUserUpdate) onUserUpdate({ ...user, profile_picture_url: file_url });
    } catch (err) {
      setPicError('Could not upload image. Please try again.');
    } finally {
      setUploadingPic(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (!user?.email) return;
    setNewsletterStatus('checking');
    base44.functions.invoke('checkNewsletterSubscription', { email: user.email })
      .then(res => setNewsletterStatus(res.data?.subscribed ? 'subscribed' : 'not_subscribed'))
      .catch(() => setNewsletterStatus('not_subscribed'));
  }, [user?.email]);

  const handleSubscribe = async () => {
    setNewsletterBusy(true);
    try {
      await base44.functions.invoke('subscribeToNewsletter', { email: user.email, source: 'account' });
      setNewsletterStatus('subscribed');
    } catch {}
    setNewsletterBusy(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSaveSuccess(false);
    try {
      const tidy = tidyProfile({
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        address_line1: form.delivery_address_line1,
        address_line2: form.delivery_address_line2,
        city: form.delivery_city,
        postcode: form.delivery_postcode,
      });
      await base44.auth.updateMe({
        first_name: tidy.first_name,
        last_name: tidy.last_name,
        full_name: `${tidy.first_name} ${tidy.last_name}`.trim(),
        phone: tidy.phone,
        delivery_address_line1: tidy.address_line1,
        delivery_address_line2: tidy.address_line2,
        delivery_city: tidy.city,
        delivery_postcode: tidy.postcode,
      });
      // Show the tidied values straight away so the customer sees exactly what
      // was saved.
      setForm({
        ...form,
        first_name: tidy.first_name,
        last_name: tidy.last_name,
        phone: tidy.phone,
        delivery_address_line1: tidy.address_line1,
        delivery_address_line2: tidy.address_line2,
        delivery_city: tidy.city,
        delivery_postcode: tidy.postcode,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg({ type: '', text: '' });
    if (!pw.current || !pw.next) { setPwMsg({ type: 'error', text: 'Enter your current and new password.' }); return; }
    if (pw.next.length < 6) { setPwMsg({ type: 'error', text: 'New password must be at least 6 characters.' }); return; }
    if (pw.next !== pw.confirm) { setPwMsg({ type: 'error', text: 'New passwords do not match.' }); return; }
    setPwLoading(true);
    try {
      await base44.auth.changePassword({ userId: user.id, currentPassword: pw.current, newPassword: pw.next });
      setPwMsg({ type: 'success', text: 'Password updated successfully.' });
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err?.message || 'Could not update password. Please check your current password.' });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="space-y-10 max-w-2xl">
      <form onSubmit={handleSave} className="space-y-8">
        {saveSuccess && (
          <div className="flex items-center gap-2 text-green-400 text-xs bg-green-400/10 border border-green-400/20 px-3 py-2">
            <CheckCircle2 className="w-4 h-4" /> Profile updated successfully.
          </div>
        )}
        {error && (
          <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2">{error}</p>
        )}

        {/* Personal Details */}
        <div>
          <SectionHead icon={User} label="PERSONAL DETAILS" />
          <div className="flex items-center gap-4 mb-6">
            <label className="relative cursor-pointer group shrink-0">
              <TierAvatar email={user?.email} profilePictureUrl={user?.profile_picture_url} size={72} />
              <span className="absolute inset-0 rounded-full flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingPic ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
              </span>
              <input type="file" accept="image/*" className="hidden" onChange={handlePictureUpload} disabled={uploadingPic} />
            </label>
            <div>
              <p className="text-white text-sm font-bold">Profile picture</p>
              <p className="text-white/30 text-xs mt-0.5">Click the avatar to upload a new photo. Your loyalty tier trophy shows at the corner.</p>
              {picError && <p className="text-red-400 text-xs mt-1">{picError}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="FIRST NAME">
              <Input value={form.first_name} onChange={set('first_name')} className={inputCls} placeholder="Your first name" />
            </Field>
            <Field label="LAST NAME">
              <Input value={form.last_name} onChange={set('last_name')} className={inputCls} placeholder="Your last name" />
            </Field>
            <Field label="EMAIL (READ-ONLY)">
              <Input value={user?.email || ''} className={`${inputCls} opacity-40 cursor-not-allowed`} readOnly />
            </Field>
            <Field label="PHONE NUMBER">
              <Input value={form.phone} onChange={set('phone')} className={inputCls} placeholder="Your phone number" />
            </Field>
          </div>
        </div>

        {/* Delivery Address */}
        <div>
          <SectionHead icon={MapPin} label="DELIVERY ADDRESS" />
          <div className="space-y-4">
            <Field label="ADDRESS LINE 1">
              <Input value={form.delivery_address_line1} onChange={set('delivery_address_line1')} className={inputCls} placeholder="Street address" />
            </Field>
            <Field label="ADDRESS LINE 2">
              <Input value={form.delivery_address_line2} onChange={set('delivery_address_line2')} className={inputCls} placeholder="Apartment, suite, etc. (optional)" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="CITY">
                <Input value={form.delivery_city} onChange={set('delivery_city')} className={inputCls} placeholder="City" />
              </Field>
              <Field label="POSTCODE">
                <Input value={form.delivery_postcode} onChange={set('delivery_postcode')} className={inputCls} placeholder="Postcode" />
              </Field>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm py-4 px-6 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {loading ? 'SAVING...' : 'SAVE CHANGES'}
        </button>
      </form>

      {/* Newsletter Subscription Status */}
      <div>
        <SectionHead icon={Mail} label="NEWSLETTER SUBSCRIPTION" />
        {newsletterStatus === 'checking' ? (
          <div className="flex items-center gap-2 text-white/40 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your subscription…
          </div>
        ) : newsletterStatus === 'subscribed' ? (
          <div className="flex items-center gap-3 bg-green-400/5 border border-green-400/20 px-4 py-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
            <div>
              <p className="text-green-400 text-sm font-bold">You're subscribed to our mailing list</p>
              <p className="text-white/40 text-xs mt-0.5">You'll receive offers, new products and event news. To unsubscribe, reply to any newsletter email or contact us.</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between bg-white/[0.02] border border-white/5 px-4 py-3">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-white/30 shrink-0" />
              <div>
                <p className="text-white/60 text-sm font-bold">You're not subscribed</p>
                <p className="text-white/30 text-xs mt-0.5">Get offers, new products and event news.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSubscribe}
              disabled={newsletterBusy}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-4 py-2.5 disabled:opacity-50"
            >
              {newsletterBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              {newsletterBusy ? 'SUBSCRIBING…' : 'SUBSCRIBE'}
            </button>
          </div>
        )}
      </div>

      {/* Change Password */}
      <form onSubmit={handlePasswordChange} className="space-y-5">
        <SectionHead icon={KeyRound} label="CHANGE PASSWORD" />
        {pwMsg.text && (
          <p className={`text-xs px-3 py-2 border ${pwMsg.type === 'success' ? 'text-green-400 bg-green-400/10 border-green-400/20' : 'text-red-400 bg-red-400/10 border-red-400/20'}`}>
            {pwMsg.text}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Field label="CURRENT PASSWORD">
            <div className="relative">
              <Input
                type={showPw.current ? 'text' : 'password'}
                value={pw.current}
                onChange={e => setPw({ ...pw, current: e.target.value })}
                className={`${inputCls} pr-9`}
                placeholder="••••••"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPw({ ...showPw, current: !showPw.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                {showPw.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
          <Field label="NEW PASSWORD">
            <div className="relative">
              <Input
                type={showPw.next ? 'text' : 'password'}
                value={pw.next}
                onChange={e => setPw({ ...pw, next: e.target.value })}
                className={`${inputCls} pr-9`}
                placeholder="Min. 6 characters"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShowPw({ ...showPw, next: !showPw.next })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                {showPw.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
          <Field label="CONFIRM NEW PASSWORD">
            <div className="relative">
              <Input
                type={showPw.confirm ? 'text' : 'password'}
                value={pw.confirm}
                onChange={e => setPw({ ...pw, confirm: e.target.value })}
                className={`${inputCls} pr-9`}
                placeholder="Repeat new password"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShowPw({ ...showPw, confirm: !showPw.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                {showPw.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
        </div>
        <button
          type="submit"
          disabled={pwLoading}
          className="flex items-center justify-center gap-2 border border-white/10 hover:border-primary/40 text-white/70 hover:text-white font-bold text-xs tracking-widest py-3.5 px-6 transition-colors disabled:opacity-50"
        >
          {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          {pwLoading ? 'UPDATING…' : 'UPDATE PASSWORD'}
        </button>
      </form>
    </div>
  );
}